import {
  type InvoiceListItemDto,
  PaginationMeta,
  ServiceTypeEnum,
  type Statement,
} from '@sr-sdks/api-sdk-axios';
import { Alert, PaginationRange } from '@sr/ui-library';
import { keepPreviousData } from '@tanstack/react-query';
import type { ComponentProps } from 'react';
import { useInvoices } from '../../../api/payments/invoiceApi';
import { useStatements } from '../../../api/payments/statementApi';
import {
  AppPagination,
  AppPaginationFooter,
} from '../../../components/AppPagination';
import { AppSearchInput } from '../../../components/AppSearchInput';
import AppTableV2, {
  AppTableV2HeaderCell,
} from '../../../components/AppTableV2';
import { ServiceBadge } from '../../../components/Badges/ServiceBadge';
import CurrencyFormatter from '../../../components/CurrencyFormatter';
import DateFormatter from '../../../components/DateFormatter';
import { DownloadCsvButton } from '../../../components/DownloadCsvButton';
import { PageHeaderActions } from '../../../components/PageHeaderActionsSlot';
import { RefreshPageButton } from '../../../components/RefreshPageButton';
import {
  royaltiesAnzTitleSearchPlaceholder,
  royaltiesWorldTitleSearchPlaceholder,
} from '../../../constant/paymentsSearchPlaceholders';
import { royaltiesAnzCopy } from '../../../constant/royaltiesAnzCopy';
import { royaltiesWorldCopy } from '../../../constant/royaltiesWorldCopy';
import { useIsSearchFetching } from '../../../hooks/useDebouncedUrlParam';
import { usePaginationBounds } from '../../../hooks/usePaginationBounds';
import {
  type RoyaltiesListEmptyCopy,
  useRoyaltiesTableState,
} from '../../../hooks/useRoyaltiesTableState';
import { useRoyaltyFileDownload } from '../../../hooks/useRoyaltyFileDownload';
import {
  ROYALTIES_LIST_TABLES,
  ROYALTIES_TITLE_EARNINGS_TABS,
  type RoyaltiesRegion,
  type RoyaltiesSection,
  type RoyaltiesTableKey,
  royaltiesTableKeys,
} from '../../../layout/royaltiesLayoutConfig';
import { STATEMENT_FILE_DOWNLOADS } from '../../../utils/api/statementFileDownloads';
import {
  type CsvExportSource,
  isCsvExportDisabled,
} from '../../../utils/csv/csvExport';
import type { CsvColumn } from '../../../utils/csv/toCsvChunk';
import { formatSeasonNo } from '../../../utils/helpers/common';
import {
  type StatementFileFlags,
  type StatementFileSource,
  getStatementFiles,
} from '../../../utils/helpers/statementFiles';
import { StatementDownloadButtons } from './StatementDownloadButtons';
import {
  ROYALTIES_EXPORT_PAGE,
  type RoyaltiesListFilters,
  earningsStatementsCsvColumns,
  earningsStatementsQueryParams,
  fetchEarningsStatementsCsvPage,
  fetchTitleStatementsCsvPage,
  titleStatementsCsvColumns,
  titleStatementsQueryParams,
} from './royaltiesCsvSpec';

export const ROYALTIES_PAGE_SIZE = 25;

export type RoyaltiesListQueryParams = RoyaltiesListFilters & {
  page: number;
  pageSize: number;
};

export type RoyaltiesListRow = StatementFileFlags & {
  _id: string;
  amountNet: number;
  currency?: string;
  date: string;
  payoutRefNo?: string;
  referenceNo?: string;
  seasonNo?: string;
  titleName?: string;
};

type RoyaltiesListQuery<TItem> = {
  data?: { items?: TItem[]; meta?: PaginationMeta | null };
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
  /** keepPreviousData: the rows on screen may not match the current params. */
  isPlaceholderData?: boolean;
  isSuccess: boolean;
};

type RoyaltiesListChromeCopy = RoyaltiesListEmptyCopy & {
  loadError: {
    bodyAfterRetry: string;
    bodyBeforeRetry: string;
    title: string;
  };
  refreshError: {
    bodyAfterRetry: string;
    bodyBeforeRetry: string;
    title: string;
  };
};

type CsvRow<TItem> = TItem & { _id: string };

type CsvFetchPage<TItem> = CsvExportSource<CsvRow<TItem>, void>['fetchPage'];

type RoyaltiesListChromeProps<TItem, TSource extends StatementFileSource> = {
  /** Defaults to the copy for `product`; override only for chrome-injection tests. */
  copy?: RoyaltiesListChromeCopy;
  /** The CSV export for this table, sharing the filters on screen. */
  csv: {
    columns: CsvColumn<CsvRow<TItem>>[];
    fetchPage: (
      filters: RoyaltiesListFilters,
      ...page: Parameters<CsvFetchPage<TItem>>
    ) => ReturnType<CsvFetchPage<TItem>>;
    fileLabel: string;
    /** The tab's on-screen name: the file holds only this tab's rows. */
    label: string;
  };
  dateSortProperty: string;
  defaultSortBy: string;
  mapRow: (item: TItem) => RoyaltiesListRow;
  /** Defaults to `anz` for chrome-injection tests. */
  product?: RoyaltiesRegion;
  referenceSortProperty: string;
  searchKey: string;
  searchPlaceholder: string;
  showPayoutReferenceColumn?: boolean;
  showTitleColumn?: boolean;
  /** The download API this list calls, which also picks the file names. */
  source: TSource;
  tableKey: RoyaltiesTableKey;
  useListQuery: (params: RoyaltiesListQueryParams) => RoyaltiesListQuery<TItem>;
  workspaceId: string;
};

type RoyaltiesListSectionProps = {
  /** Whether the active workspace is an agent (drives Title search copy). */
  isAgent?: boolean;
  /**
   * Required on the production path: a caller that forgets it would otherwise
   * render ANZ data under a World URL with no error. Optional only on the
   * chrome props below, which exist as a test seam.
   */
  product: RoyaltiesRegion;
  section: RoyaltiesSection;
  workspaceId: string;
};

type RoyaltiesHeaderCellProps = ComponentProps<typeof AppTableV2HeaderCell> & {
  sortByKey: string;
};

/**
 * Repasses `sortByKey` to narrow it optional -> required. Without that, a
 * header cell missing the prop falls back to AppTableV2HeaderCell's
 * DEFAULT_SORT_BY_KEY and ANZ and World collide on sort state.
 */
const RoyaltiesHeaderCell = ({
  sortByKey,
  ...props
}: RoyaltiesHeaderCellProps) => (
  <AppTableV2HeaderCell {...props} sortByKey={sortByKey} />
);

/**
 * Error banner shared by the empty-load and stale-refresh states, which differ
 * only in which copy block they read.
 */
const RoyaltiesErrorAlert = ({
  copy,
}: {
  copy: RoyaltiesListChromeCopy['loadError'];
}) => (
  <Alert title={copy.title} variant="error">
    <p className="text-sm">
      {copy.bodyBeforeRetry}
      <RefreshPageButton />
      {copy.bodyAfterRetry}
    </p>
  </Alert>
);

/**
 * Region assumed when `product` is omitted. Both route call sites pass it
 * explicitly; this only covers the chrome-injection test overload.
 */
const DEFAULT_ROYALTIES_REGION: RoyaltiesRegion = 'anz';

const PRODUCT_COPY = {
  anz: royaltiesAnzCopy,
  world: royaltiesWorldCopy,
} as const;

/** Region -> API serviceType filter, sent by the list and its CSV export alike. */
export const REGION_SERVICE_TYPE = {
  anz: ServiceTypeEnum.RoyaltiesAnz,
  world: ServiceTypeEnum.RoyaltiesWorld,
} as const satisfies Record<RoyaltiesRegion, ServiceTypeEnum>;

/** A tab's on-screen name, so its CSV dialog names what the file holds (D24). */
const tabLabel = (
  section: (typeof ROYALTIES_TITLE_EARNINGS_TABS)[number]['section'],
) =>
  ROYALTIES_TITLE_EARNINGS_TABS.find((tab) => tab.section === section)?.label ??
  section;

const titleSearchPlaceholder = (product: RoyaltiesRegion, isAgent: boolean) =>
  product === 'anz'
    ? royaltiesAnzTitleSearchPlaceholder(isAgent)
    : royaltiesWorldTitleSearchPlaceholder(isAgent);

/**
 * Shared royalties list chrome (search, pagination, downloads). Title vs
 * Earnings only differ by query, column set, and download endpoint. ANZ vs
 * World differ by serviceType, URL keys, and empty/error copy.
 */
const RoyaltiesListChrome = <TItem, TSource extends StatementFileSource>({
  copy,
  csv,
  dateSortProperty,
  defaultSortBy,
  mapRow,
  product = DEFAULT_ROYALTIES_REGION,
  referenceSortProperty,
  searchKey,
  searchPlaceholder,
  showPayoutReferenceColumn = false,
  showTitleColumn = false,
  source,
  tableKey,
  useListQuery,
  workspaceId,
}: RoyaltiesListChromeProps<TItem, TSource>) => {
  // Derived from `product` so the two cannot disagree — an explicit `product`
  // with a forgotten `copy` used to render ANZ empty-state text on a World table.
  const resolvedCopy = copy ?? PRODUCT_COPY[product];
  const { paginationKey, sortByKey, tableId } = royaltiesTableKeys(
    product,
    tableKey,
  );

  const {
    customEmptyTable,
    debouncedSearch,
    handlePageRequest,
    page,
    search,
    searchInput,
    setPageAndUrl,
    setSearchInput,
    sortBy,
  } = useRoyaltiesTableState({
    copy: resolvedCopy,
    defaultSortBy,
    paginationKey,
    searchKey,
    sortByKey,
  });

  const download = useRoyaltyFileDownload(STATEMENT_FILE_DOWNLOADS[source]);

  // One object for the list and its CSV export, so the file can't drift from
  // the table. The query builders add the sort tiebreaker.
  const filters: RoyaltiesListFilters = {
    searchTerm: search,
    serviceType: REGION_SERVICE_TYPE[product],
    sortBy,
    workspaceId,
  };

  const listQuery = useListQuery({
    ...filters,
    page,
    pageSize: ROYALTIES_PAGE_SIZE,
  });

  const rows = (listQuery.data?.items ?? []).map(mapRow);
  const paginationMeta = listQuery.data?.meta ?? null;
  const totalPages = paginationMeta?.totalPages ?? 0;
  const totalItems = paginationMeta?.totalItems ?? 0;

  const { clampedPage, isPageOutOfRange } = usePaginationBounds({
    isSuccess: listQuery.isSuccess,
    page,
    setPage: setPageAndUrl,
    totalItems,
    totalPages,
  });

  const trimmedSearch = search.trim();
  const hasNothingToSearch =
    rows.length === 0 && !trimmedSearch && !listQuery.isError;
  // Deliberately not disabled on isError: keepPreviousData may still show a
  // stale list, and disabling would strip every way to retry (ClientsV3).
  const areControlsDisabled = listQuery.isPending || hasNothingToSearch;

  const isSearchFetching = useIsSearchFetching(searchInput, debouncedSearch, {
    isFetching: listQuery.isFetching,
    isPending: listQuery.isPending,
  });

  const { loadError, refreshError } = resolvedCopy;
  const showEmptyError = listQuery.isError && !listQuery.data;
  const showStaleError = listQuery.isError && Boolean(listQuery.data);

  // `disabled` is the only prop that differs between the two placements: the
  // error state deliberately leaves search enabled so the user can retry.
  const renderSearchInput = (disabled: boolean) => (
    <AppSearchInput
      classes={{ wrapper: 'md:w-96' }}
      disabled={disabled}
      isFetching={isSearchFetching}
      placeholder={searchPlaceholder}
      value={searchInput}
      onChange={setSearchInput}
    />
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeaderActions>
        <DownloadCsvButton
          label={csv.label}
          columns={csv.columns}
          fetchPage={(exportPage, pageSize, signal) =>
            csv.fetchPage(filters, exportPage, pageSize, signal)
          }
          fileLabel={csv.fileLabel}
          isDisabled={isCsvExportDisabled(listQuery, totalItems)}
          rowCount={totalItems}
          scope="section"
        />
      </PageHeaderActions>
      {showEmptyError ? (
        <>
          {renderSearchInput(false)}
          <RoyaltiesErrorAlert copy={loadError} />
        </>
      ) : (
        <>
          {showStaleError && <RoyaltiesErrorAlert copy={refreshError} />}
          <AppTableV2
            id={tableId}
            customEmptyTable={customEmptyTable}
            customPageCountElement={
              // shrink-0 is scoped here rather than to the shared AppTableV2
              // wrapper: this cell competes with a `w-full` search input, and
              // other callers pass elements that need to stay shrinkable.
              <div className="flex shrink-0 items-center gap-4">
                <PaginationRange
                  page={clampedPage}
                  pageSize={ROYALTIES_PAGE_SIZE}
                  totalItems={totalItems}
                />
                <AppPagination
                  currentPage={clampedPage}
                  disabled={areControlsDisabled}
                  totalPages={totalPages}
                  onPageChange={setPageAndUrl}
                />
              </div>
            }
            filterElement={renderSearchInput(areControlsDisabled)}
            headerRowElement={
              <tr>
                <RoyaltiesHeaderCell
                  sortByKey={sortByKey}
                  sortByProperty={dateSortProperty}
                >
                  Date
                </RoyaltiesHeaderCell>
                <RoyaltiesHeaderCell
                  sortByKey={sortByKey}
                  sortByProperty={referenceSortProperty}
                >
                  Reference ID
                </RoyaltiesHeaderCell>
                {showPayoutReferenceColumn && (
                  <RoyaltiesHeaderCell sortByKey={sortByKey}>
                    Payout Reference ID
                  </RoyaltiesHeaderCell>
                )}
                {showTitleColumn && (
                  <>
                    <RoyaltiesHeaderCell
                      sortByKey={sortByKey}
                      sortByProperty="beneficiaryTitleName"
                    >
                      Title
                    </RoyaltiesHeaderCell>
                    <RoyaltiesHeaderCell sortByKey={sortByKey}>
                      Season
                    </RoyaltiesHeaderCell>
                  </>
                )}
                {/* Single serviceType filter per product — sorting by service is a no-op. */}
                <RoyaltiesHeaderCell sortByKey={sortByKey}>
                  Service
                </RoyaltiesHeaderCell>
                <RoyaltiesHeaderCell
                  className="!text-center"
                  sortByKey={sortByKey}
                  sortByProperty="amountNet"
                >
                  Amount
                </RoyaltiesHeaderCell>
                <RoyaltiesHeaderCell
                  className="!text-center"
                  sortByKey={sortByKey}
                  sortByProperty="currency"
                >
                  Currency
                </RoyaltiesHeaderCell>
                <RoyaltiesHeaderCell
                  className="!text-center"
                  sortByKey={sortByKey}
                >
                  Download
                </RoyaltiesHeaderCell>
              </tr>
            }
            isLoading={listQuery.isPending || isPageOutOfRange}
            pageRequestDeps="pageAndSort"
            paginationKey={paginationKey}
            paginationMeta={paginationMeta}
            sortByKey={sortByKey}
            onPageRequest={handlePageRequest}
          >
            {rows.map((row) => (
              <tr key={row._id}>
                <td>
                  <DateFormatter dateString={row.date} />
                </td>
                <td>{row.referenceNo || '—'}</td>
                {showPayoutReferenceColumn && (
                  <td className="whitespace-nowrap">
                    {row.payoutRefNo || '—'}
                  </td>
                )}
                {showTitleColumn && (
                  <>
                    <td>{row.titleName || 'Unknown Title'}</td>
                    <td className="whitespace-nowrap">
                      {formatSeasonNo(row.seasonNo)}
                    </td>
                  </>
                )}
                <td>
                  <ServiceBadge serviceType={row.serviceType} />
                </td>
                <td className="!text-center">
                  <CurrencyFormatter amount={row.amountNet} />
                </td>
                <td className="!text-center">{row.currency}</td>
                <td className="!text-center">
                  <StatementDownloadButtons
                    download={download}
                    files={getStatementFiles(row, source)}
                    gap={2}
                    row={row}
                  />
                </td>
              </tr>
            ))}
          </AppTableV2>

          <AppPaginationFooter
            currentPage={clampedPage}
            disabled={areControlsDisabled}
            pageSize={ROYALTIES_PAGE_SIZE}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={setPageAndUrl}
          />
        </>
      )}
    </div>
  );
};

const mapStatementRow = (statement: Statement): RoyaltiesListRow => ({
  _id: statement._id,
  amountNet: statement.amountNet,
  currency: statement.currency,
  date: statement.statementDate,
  hasDisbursementStatementPdf: statement.hasDisbursementStatementPdf,
  hasRoyaltyStatementCsv: statement.hasRoyaltyStatementCsv,
  hasRoyaltyStatementPdf: statement.hasRoyaltyStatementPdf,
  referenceNo: statement.statementNo,
  seasonNo: statement.beneficiarySeasonNo,
  // serviceType comes from the backend statement, which is already filtered by
  // the API query. This respects what the server returns, even if unexpected.
  serviceType: statement.serviceType,
  titleName: statement.beneficiaryTitleName,
});

const mapInvoiceRow = (invoice: InvoiceListItemDto): RoyaltiesListRow => ({
  _id: invoice._id,
  amountNet: invoice.amountNet,
  currency: invoice.currency,
  date: invoice.invoiceDate,
  hasDisbursementStatementPdf: invoice.hasDisbursementStatementPdf,
  hasRoyaltyStatementCsv: invoice.hasRoyaltyStatementCsv,
  hasRoyaltyStatementPdf: invoice.hasRoyaltyStatementPdf,
  payoutRefNo: invoice.payoutRefNo,
  referenceNo: invoice.invoiceNo,
  serviceType: invoice.serviceType,
});

const useTitleStatementsListQuery = ({
  page,
  pageSize,
  ...filters
}: RoyaltiesListQueryParams) =>
  useStatements(titleStatementsQueryParams(filters, page, pageSize), {
    enabled: !!filters.workspaceId,
    placeholderData: keepPreviousData,
  });

const useEarningsListQuery = ({
  page,
  pageSize,
  ...filters
}: RoyaltiesListQueryParams) =>
  useInvoices(earningsStatementsQueryParams(filters, page, pageSize), {
    enabled: !!filters.workspaceId,
    placeholderData: keepPreviousData,
  });

const TitleStatementsTable = ({
  isAgent = false,
  product,
  workspaceId,
}: {
  isAgent?: boolean;
  product: RoyaltiesRegion;
  workspaceId: string;
}) => {
  // Spread so a new field on ROYALTIES_TABLE_SHAPE reaches the chrome without
  // editing both section components. `searchPlaceholder` is overridden below
  // (agent copy differs); the config's unused `agentSearchPlaceholder` is
  // simply not destructured by the chrome.
  const config = ROYALTIES_LIST_TABLES[product]['title-statements'];

  return (
    <RoyaltiesListChrome
      {...config}
      csv={{
        columns: titleStatementsCsvColumns,
        fetchPage: fetchTitleStatementsCsvPage,
        fileLabel: ROYALTIES_EXPORT_PAGE[product].title,
        label: tabLabel('title-statements'),
      }}
      mapRow={mapStatementRow}
      product={product}
      searchPlaceholder={titleSearchPlaceholder(product, isAgent)}
      source="statement"
      useListQuery={useTitleStatementsListQuery}
      workspaceId={workspaceId}
    />
  );
};

const EarningsTable = ({
  product,
  workspaceId,
}: {
  product: RoyaltiesRegion;
  workspaceId: string;
}) => {
  const config = ROYALTIES_LIST_TABLES[product]['earnings-statements'];

  return (
    <RoyaltiesListChrome
      {...config}
      csv={{
        columns: earningsStatementsCsvColumns,
        fetchPage: fetchEarningsStatementsCsvPage,
        fileLabel: ROYALTIES_EXPORT_PAGE[product].earnings,
        label: tabLabel('earnings-statements'),
      }}
      mapRow={mapInvoiceRow}
      product={product}
      source="invoice"
      useListQuery={useEarningsListQuery}
      workspaceId={workspaceId}
    />
  );
};

/**
 * Royalties list (ANZ or World). Production call sites pass `section`
 * (stable per route, so the title vs earnings hook never swaps under a live
 * mount) and optional `product` (defaults to `anz`). Tests inject chrome props
 * (`useListQuery` / `mapRow` / …) to cover shared UI without MSW.
 */
export function RoyaltiesListTable(
  props: RoyaltiesListSectionProps,
): JSX.Element;
export function RoyaltiesListTable<TItem, TSource extends StatementFileSource>(
  props: RoyaltiesListChromeProps<TItem, TSource>,
): JSX.Element;
export function RoyaltiesListTable<TItem, TSource extends StatementFileSource>(
  props: RoyaltiesListChromeProps<TItem, TSource> | RoyaltiesListSectionProps,
) {
  if ('section' in props) {
    const { isAgent, product, section, workspaceId } = props;

    return section === 'title-statements' ? (
      <TitleStatementsTable
        isAgent={isAgent}
        product={product}
        workspaceId={workspaceId}
      />
    ) : (
      <EarningsTable product={product} workspaceId={workspaceId} />
    );
  }

  return <RoyaltiesListChrome {...props} />;
}
